import { useMemo, useState } from "react";
import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Truck, Radio, CalendarRange, Leaf, Route as RouteIcon, Cloud } from "lucide-react";
import { fetcher } from "@/lib/api";
import FrotaCard from "@/components/frota/FrotaCard";
import FrotaDetailDrawer from "@/components/frota/FrotaDetailDrawer";
import { Skeleton } from "@/components/ui/skeleton";
import "@/i18n/fase8";

function StatTile({ icon: Icon, label, value, accent = "text-salnes-atlantic", testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-white border hairline p-5 flex items-center gap-4"
      data-testid={testid}
    >
      <div className="h-10 w-10 rounded-xl bg-salnes-sand/60 flex items-center justify-center">
        <Icon size={18} className={accent} strokeWidth={1.6} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone truncate">
          {label}
        </div>
        <div className="font-serif text-2xl text-salnes-ink leading-tight truncate">
          {value ?? "—"}
        </div>
      </div>
    </motion.div>
  );
}

const TYPES = ["recogida", "barrido", "lavado", "grua", "furgoneta", "inspeccion", "outro"];
const FUELS = ["diesel_b7", "glp_autogas", "gnc_gnv", "hvo_biocombustible", "electricidade_mix_es_2024"];

export default function Frota() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({ type: "all", fuel: "all", gps: "all" });
  const [selected, setSelected] = useState(null);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.type !== "all") p.set("vehicle_type", filters.type);
    if (filters.fuel !== "all") p.set("fuel_type", filters.fuel);
    if (filters.gps === "active") p.set("has_gps", "true");
    if (filters.gps === "none") p.set("has_gps", "false");
    return p.toString() ? `?${p.toString()}` : "";
  }, [filters]);

  const { data, isLoading } = useSWR(`/frota${query}`, fetcher, { refreshInterval: 90000 });
  const { data: kpis } = useSWR("/frota/aggregate/kpis", fetcher, { refreshInterval: 90000 });

  const vehicles = data?.vehicles || [];

  return (
    <div className="min-h-full bg-salnes-ivory" data-testid="frota-root">
      {/* Hero */}
      <section className="bg-gradient-to-br from-salnes-atlantic via-[#0B4C7A] to-[#082E4B] text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10 lg:py-14">
          <div className="text-[11px] uppercase tracking-[0.28em] text-salnes-emerald mb-2">
            {t("fase8.frota.nav")}
          </div>
          <h1 className="font-serif text-3xl lg:text-5xl leading-tight">
            {t("fase8.frota.title")}
          </h1>
          <p className="mt-3 text-sm lg:text-base text-white/75 max-w-2xl">
            {t("fase8.frota.subtitle")}
          </p>

          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mt-8">
            <HeroKpi icon={Truck} label={t("fase8.frota.kpi_total")} value={kpis?.total} testid="frota-kpi-total" />
            <HeroKpi icon={Radio} label={t("fase8.frota.kpi_gps")} value={kpis?.with_gps_active} testid="frota-kpi-gps" />
            <HeroKpi icon={CalendarRange} label={t("fase8.frota.kpi_avg_age")} value={kpis?.avg_age_years ? `${kpis.avg_age_years} a` : "—"} testid="frota-kpi-age" />
            <HeroKpi icon={Leaf} label={t("fase8.frota.kpi_euro6")} value={kpis?.euro6_plus_pct != null ? `${kpis.euro6_plus_pct} %` : "—"} testid="frota-kpi-euro6" />
            <HeroKpi icon={RouteIcon} label={t("fase8.frota.kpi_ytd_km")} value={kpis?.ytd_carbon?.km_traveled?.toLocaleString?.() || "—"} testid="frota-kpi-ytd-km" />
            <HeroKpi icon={Cloud} label={t("fase8.frota.kpi_ytd_co2")} value={kpis?.ytd_carbon?.kgco2e_emitted?.toLocaleString?.() || "—"} testid="frota-kpi-ytd-co2" />
          </div>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8">
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <Selector
            label={t("fase8.frota.filter_type")}
            value={filters.type}
            onChange={(v) => setFilters({ ...filters, type: v })}
            options={[{ v: "all", l: t("fase8.frota.filters_all") }, ...TYPES.map(x => ({ v: x, l: t(`fase8.frota.type_${x}`) }))]}
            testid="frota-filter-type"
          />
          <Selector
            label={t("fase8.frota.filter_fuel")}
            value={filters.fuel}
            onChange={(v) => setFilters({ ...filters, fuel: v })}
            options={[{ v: "all", l: t("fase8.frota.filters_all") }, ...FUELS.map(x => ({ v: x, l: t(`fase8.frota.fuel_${x}`) }))]}
            testid="frota-filter-fuel"
          />
          <Selector
            label={t("fase8.frota.filter_gps")}
            value={filters.gps}
            onChange={(v) => setFilters({ ...filters, gps: v })}
            options={[
              { v: "all", l: t("fase8.frota.filters_all") },
              { v: "active", l: t("fase8.frota.filter_gps_active") },
              { v: "none", l: t("fase8.frota.filter_gps_none") },
            ]}
            testid="frota-filter-gps"
          />
          <div className="ml-auto text-xs text-salnes-stone" data-testid="frota-count">
            {data?.count ?? "—"} · {kpis?.iris_only ?? "—"} IRIS
          </div>
        </div>

        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
          </div>
        ) : vehicles.length === 0 ? (
          <div className="text-center text-sm text-salnes-stone py-16">{t("fase8.frota.empty")}</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {vehicles.map((v, i) => (
              <FrotaCard
                key={v.plate}
                vehicle={v}
                index={i}
                onClick={() => setSelected(v.plate)}
              />
            ))}
          </div>
        )}
      </section>

      <FrotaDetailDrawer plate={selected} open={!!selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function HeroKpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-white/8 backdrop-blur border border-white/10 px-4 py-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/60">
        <Icon size={12} strokeWidth={1.6} /> {label}
      </div>
      <div className="font-serif text-xl lg:text-2xl leading-tight mt-1">{value ?? "—"}</div>
    </div>
  );
}

function Selector({ label, value, onChange, options, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs text-salnes-stone">
      <span className="uppercase tracking-[0.18em] text-[10px]">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border hairline rounded-lg px-3 py-1.5 text-sm text-salnes-ink focus:outline-none focus:ring-2 focus:ring-salnes-emerald"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>
    </label>
  );
}
